import { BlockingGate, BlockingModal } from '@/shared/components/BlockingGate';
import CustomButton from '@/shared/components/CustomButton';
import { Checkbox } from '@smartech/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { TERMS_AND_CONDITIONS } from '../constants/termsContent';
import { useTermsAndConditions } from '../hooks/useTermsAndConditions';

const EMAIL_PATTERN = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

function renderParagraphWithEmailLinks(paragraph: string) {
  return paragraph.split(EMAIL_PATTERN).map((part, index) => {
    if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(part)) {
      return (
        <a
          key={`${part}-${index}`}
          href={`mailto:${part}`}
          className="font-medium text-button-fg-tertiary underline dir-ltr hover:text-brand-700"
        >
          {part}
        </a>
      );
    }

    return part;
  });
}

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  isSubmitting: boolean;
  onConfirm: () => void;
}

function TermsAndConditionsModal({
  isOpen,
  isSubmitting,
  onConfirm,
}: TermsAndConditionsModalProps) {
  const [hasReadTerms, setHasReadTerms] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setHasReadTerms(false);
    }
  }, [isOpen]);

  return (
    <BlockingModal
      isOpen={isOpen}
      titleId="terms-and-conditions-title"
      descriptionId="terms-and-conditions-content"
      dialogClassName="max-h-[90vh] max-w-3xl"
    >
      <div className="relative z-10 mb-4xl border-b border-border-secondary pb-4xl">
        <h2
          id="terms-and-conditions-title"
          className="text-lg font-semibold text-text-primary-900 lg:text-xl"
        >
          {TERMS_AND_CONDITIONS.title}
        </h2>
        <p className="mt-sm text-sm text-text-tertiary-600">
          برای ادامه استفاده از افیلیو، لطفاً متن زیر را مطالعه کرده و تایید کنید.
        </p>
      </div>

      <div
        id="terms-and-conditions-content"
        className="relative z-10 min-h-0 flex-1 overflow-y-auto pr-sm"
      >
        {TERMS_AND_CONDITIONS.sections.map((section) => (
          <section key={section.heading} className="mb-5xl last:mb-0">
            <h3 className="mb-md text-md font-semibold text-text-primary-900">{section.heading}</h3>
            <div className="space-y-md">
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph} className="text-sm leading-6 text-text-secondary-700">
                  {renderParagraphWithEmailLinks(paragraph)}
                </p>
              ))}
            </div>
          </section>
        ))}
      </div>

      <div className="relative z-10 mt-5xl border-t border-border-secondary pt-5xl">
        <div className="flex items-start justify-start gap-md">
          <Checkbox
            id="terms-read-checkbox"
            size="sm"
            checked={hasReadTerms}
            onChange={(checked) => setHasReadTerms(Boolean(checked))}
          />
          <label
            htmlFor="terms-read-checkbox"
            className="cursor-pointer text-sm font-medium text-text-secondary-700"
          >
            مطالعه کردم و با شرایط و ضوابط همکاری افیلیو موافقم.
          </label>
        </div>

        <CustomButton
          variant="primary"
          size="lg"
          className="mt-4xl w-full"
          disabled={!hasReadTerms || isSubmitting}
          loading={isSubmitting}
          onClick={onConfirm}
        >
          تایید و ادامه
        </CustomButton>
      </div>
    </BlockingModal>
  );
}

interface TermsAndConditionsGateProps {
  children: ReactNode;
}

export default function TermsAndConditionsGate({ children }: TermsAndConditionsGateProps) {
  const { isBlocking, isLoading, isSubmitting, acceptTerms } = useTermsAndConditions();
  const shouldBlockInteraction = isLoading || isBlocking;

  return (
    <BlockingGate
      isActive={shouldBlockInteraction}
      expectsDialog={isBlocking}
      isLoading={isLoading}
      dialog={
        <TermsAndConditionsModal
          isOpen={isBlocking}
          isSubmitting={isSubmitting}
          onConfirm={acceptTerms}
        />
      }
    >
      {children}
    </BlockingGate>
  );
}
